import * as z from "zod";
import { useFieldArray, UseFormReturn } from "react-hook-form";
import { Form } from "@/components/ui/form.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";
import {
  Tooltip,
  TooltipTrigger,
  TooltipProvider,
  TooltipContent,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button.tsx";
import { PlusIcon, XIcon, WrenchIcon, InfoIcon } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { H4 } from "@/components/ui/h4.tsx";

const STARTS_WITH_HTTP_OR_HTTPS_REGEX = /^https?:\/\//;

const linkItemSchema = z.object({
  link: z
    .string()
    .transform((s) =>
      STARTS_WITH_HTTP_OR_HTTPS_REGEX.test(s) ? s : `https://${s}`,
    ),

  type: z.enum(["Drive", "Web", "Github", "Doc"]).optional(),
});

export const knowledgeBasesFormSchema = z.object({
  core_personality: z.array(linkItemSchema).default([]),
  static_data: z.array(linkItemSchema).default([]),
  live_data: z.array(linkItemSchema).default([]),
  tools: z.array(z.string()).default([]),
});

export type KnowledgeBases = z.infer<typeof knowledgeBasesFormSchema>;

export function KnowledgeBasesStep({
  readOnly,
  form,
}: {
  readOnly: boolean;
  form: UseFormReturn<KnowledgeBases>;
}) {
  return (
    <Form {...form}>
      <form className="flex flex-col gap-y-1-full">
        <H4 className="text-primary mb-2">
          Build Your AI's Foundation & Capabilities
        </H4>

        <p className="text-sm text-white mt-0 mb-2 align-top">
          Shape your AI's core knowledge and capabilities by providing documents
          from Google Drive, Google Docs, GitHub repositories, or any URL. These
          materials - whether technical documentation, research papers, or
          domain knowledge - become the foundation for your AI's understanding
          and decision-making.
        </p>

        <p className="text-sm text-white mt-0 mb-7 align-top">
          Note: Please ensure that the links you provide are public and
          accessible without any authorization.
        </p>

        {/* Columns */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-6">
          <KnowledgeBaseColumnHeader
            label="Core Personality & Worldview Documents"
            tooltip="Foundational documents that define the agent's core personality and worldview."
            column={1}
          />
          <KnowledgeBaseColumn
            form={form}
            name="core_personality"
            readOnly={readOnly}
            column={1}
          />
          <KnowledgeBaseColumnHeader
            label="Knowledge Base Data - Static Data"
            tooltip="Static sources that enrich the agent's general knowledge."
            column={2}
          />
          <KnowledgeBaseColumn
            form={form}
            name="static_data"
            readOnly={readOnly}
            column={2}
          />
          <KnowledgeBaseColumnHeader
            label="Preferred News Sources - Live Data"
            tooltip="Live news sources that keep the agent up-to-date on current events."
            column={3}
          />
          <KnowledgeBaseColumn
            form={form}
            name="live_data"
            readOnly={readOnly}
            column={3}
          />
          <ToolsColumnHeader
            label="Tools"
            tooltip="Tools associated with the knowledge bases."
            column={4}
          />
          <ToolsColumn
            form={form}
            name="tools"
            readOnly={readOnly}
            column={4}
          />
        </div>
      </form>
    </Form>
  );
}

function KnowledgeBaseColumnHeader({
  label,
  tooltip,
  column,
}: {
  label: string;
  tooltip: string;
  column: number;
}) {
  const mobileRow = column * 2 - 1;

  return (
    <div
      className={`row-start-${mobileRow} row-end-${mobileRow + 1} md:row-start-1 md:row-end-2`}
    >
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex flex-row items-center">
              <Label>{label}</Label>
              <InfoIcon className="pl-1 w-4" />
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p className="max-w-64">{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

function KnowledgeBaseColumn({
  form,
  name,
  readOnly,
  column,
}: {
  form: UseFormReturn<KnowledgeBases>;
  name: "core_personality" | "static_data" | "live_data";
  readOnly: boolean;
  column: number;
}) {
  const mobileRow = column * 2;

  const fieldArray = useFieldArray({ control: form.control, name });

  const determineType = (url: string) => {
    if (url.includes("github.com")) return "Github";
    if (url.includes("drive.google.com")) return "Drive";
    if (url.includes("docs.google.com")) return "Doc";
    return "Web";
  };

  const handleInputChange = (index: number, value: string) => {
    const type = determineType(value);
    form.setValue(`${name}.${index}.type`, type, { shouldDirty: true });
  };

  const getIconForType = (type: string) => {
    switch (type) {
      case "Github":
        return "/github-icon.png";
      case "Drive":
        return "/drive-icon.png";
      case "Doc":
        return "/docs-icon.png";
      default:
        return "/internet-icon.png";
    }
  };

  return (
    <div
      className={`flex flex-col gap-y-4 row-start-${mobileRow} row-end-${mobileRow + 1} md:row-start-2 md:row-end-3`}
    >
      {fieldArray.fields.map((field, index) => (
        <div key={field.id} className="flex flex-row items-start gap-x-2">
          <img
            src={getIconForType(
              form.getValues(`${name}.${index}.type`) || "Web",
            )}
            alt={`${form.getValues(`${name}.${index}.type`)} icon`}
            className="w-6 h-6 my-1.5"
          />
          <div className="flex flex-col gap-y-1 flex-grow">
            <Input
              {...form.register(`${name}.${index}.link` as const)}
              placeholder="Enter link"
              disabled={readOnly}
              className="flex-grow"
              onChange={(e) => handleInputChange(index, e.target.value)}
            />
            {form.formState.errors?.[name]?.[index]?.link?.message ? (
              <p className={"text-[0.8rem] font-medium text-destructive"}>
                {form.formState.errors[name][index]?.link?.message}
              </p>
            ) : null}
          </div>
          {!readOnly && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => fieldArray.remove(index)}
            >
              <XIcon className="w-4 h-4" />
            </Button>
          )}
        </div>
      ))}

      {!readOnly && (
        <Button
          className="flex flex-row gap-x-1 items-center"
          type="button"
          variant="ghost"
          onClick={() => fieldArray.append({ link: "" })}
        >
          <PlusIcon className="w-4 h-4" />
          Add
        </Button>
      )}
    </div>
  );
}

function ToolsColumnHeader({
  label,
  tooltip,
  column,
}: {
  label: string;
  tooltip: string;
  column: number;
}) {
  const mobileRow = column * 2 - 1;

  return (
    <div
      className={`row-start-${mobileRow} row-end-${mobileRow + 1} md:row-start-1 md:row-end-2`}
    >
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex flex-row items-center">
              <Label>{label}</Label>
              <InfoIcon className="pl-1 w-4" />
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p className="max-w-64">{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

function ToolsColumn({
  form,
  name,
  readOnly,
  column,
}: {
  form: UseFormReturn<KnowledgeBases>;
  name: keyof KnowledgeBases;
  readOnly: boolean;
  column: number;
}) {
  const mobileRow = column * 2 - 1;

  // @ts-expect-error types seem to be wrong
  const fieldArray = useFieldArray({ control: form.control, name });

  return (
    <div
      className={`flex flex-col gap-y-4 row-start-${mobileRow} row-end-${mobileRow + 1} md:row-start-2 md:row-end-3`}
    >
      {fieldArray.fields.map((field, index) => (
        <div key={field.id} className="flex flex-row items-center gap-x-2">
          <WrenchIcon className="w-6 h-6 text-gray-500" />
          <div className="w-full">
            <Select
              // @ts-expect-error types seem to be wrong
              defaultValue={form.getValues(`${name}.${index}`) || ""}
              onValueChange={(value) =>
                form.setValue(`${name}.${index}`, value, { shouldDirty: true })
              }
              disabled={readOnly}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Tool" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="InternetSearch">Internet Search</SelectItem>
                <SelectItem value="DefiDeepQ" disabled>
                  PolyMarket (Coming Soon)
                </SelectItem>
                <SelectItem value="DeepQData" disabled>
                  Defi Market Data by DeepQDigital (Coming Soon)
                </SelectItem>
                <SelectItem value="Portfolio" disabled>
                  Portfolio Analysis by ArchIndices (Coming Soon)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          {!readOnly && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => fieldArray.remove(index)}
            >
              <XIcon className="w-4 h-4" />
            </Button>
          )}
        </div>
      ))}

      {!readOnly && (
        <Button
          className="flex flex-row gap-x-1 items-center"
          type="button"
          variant="ghost"
          // @ts-expect-error types seem to be wrong
          onClick={() => fieldArray.append("")}
        >
          <PlusIcon className="w-4 h-4" />
          Add Tool
        </Button>
      )}
    </div>
  );
}
